import { motion } from "motion/react";
import { useEffect, useRef } from "react";
import { getCatalog } from "../i18n";
import { useMotionPolicy } from "../lib/hooks/useMotionPolicy";
import { usePresentedSnapshot } from "../presentation/hooks";
import { useNexusStore } from "../store/nexusStore";
import { softSpring, staggerItem } from "../lib/motion";

const HERO_IDLE_MS = 9000;

export function HeroStage() {
  const snapshot = usePresentedSnapshot();
  const locale = useNexusStore((state) => state.locale);
  const setCommandOpen = useNexusStore((state) => state.setCommandOpen);
  const focus = useNexusStore((state) => state.focus);
  const focusCosmos = useNexusStore((state) => state.focusCosmos);
  const motionPolicy = useMotionPolicy();
  const ui = getCatalog(locale).ui;
  const heroVisible = useNexusStore((state) => state.heroVisible);
  const setHeroVisible = useNexusStore((state) => state.setHeroVisible);
  const demoTourActive = useNexusStore((state) => state.demoTourActive);
  const setDemoTourActive = useNexusStore((state) => state.setDemoTourActive);
  const timeoutRef = useRef<number | null>(null);

  const systemCount = snapshot?.nodes.filter((node) => node.kind === "system").length ?? 0;
  const onlineCount = snapshot?.nodes.filter((node) => node.kind === "system" && node.status !== "offline").length ?? 0;

  useEffect(() => {
    if (focus.kind !== "cosmos") {
      setHeroVisible(false);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      return;
    }

    const scheduleReveal = () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        const state = useNexusStore.getState();
        if (state.focus.kind === "cosmos" && !state.commandOpen && !state.demoTourActive) state.setHeroVisible(true);
      }, HERO_IDLE_MS);
    };

    const onSceneNavigation = () => {
      const state = useNexusStore.getState();
      if (state.focus.kind !== "cosmos" || state.demoTourActive) return;
      state.setHeroVisible(false);
      scheduleReveal();
    };

    if (!heroVisible && !demoTourActive) scheduleReveal();

    window.addEventListener("knexus:scene-navigation", onSceneNavigation);
    return () => {
      window.removeEventListener("knexus:scene-navigation", onSceneNavigation);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, [focus.kind, heroVisible, demoTourActive, setHeroVisible]);

  useEffect(() => {
    if (focus.kind === "cosmos" && !demoTourActive) return;
    setHeroVisible(false);
  }, [focus.kind, demoTourActive, setHeroVisible]);

  const heroVariants = motionPolicy.reduced
    ? { hidden: { opacity: 0 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 20, transition: { staggerChildren: 0.025, staggerDirection: -1 as const } },
        show: { opacity: 1, y: 0, transition: { ...softSpring, staggerChildren: 0.055, delayChildren: 0.035 } },
      };

  return (
    <motion.section
      className={`hero-stage${heroVisible ? " is-visible" : " is-hidden"}`}
      initial="hidden"
      animate={heroVisible ? "show" : "hidden"}
      variants={heroVariants}
      aria-label={ui.brandTagline}
      aria-hidden={!heroVisible}
    >
      <motion.div className="hero-stage__eyebrow" variants={staggerItem}>
        <span className="signal-dot" aria-hidden="true" />
        <b>{ui.heroEyebrow}</b>
        <small>
          {onlineCount.toString().padStart(2, "0")}/{systemCount.toString().padStart(2, "0")}
        </small>
      </motion.div>
      <motion.h1 variants={staggerItem}>
        <span>{ui.heroHeadlineLine1}</span>
        <span>
          {ui.heroHeadlineLine2Prefix} <em>{ui.heroHeadlineLine2Accent}</em>
        </span>
      </motion.h1>
      <motion.p variants={staggerItem}>{ui.heroDescription}</motion.p>
      <motion.div className="hero-stage__actions" variants={staggerItem}>
        <button
          className="hero-cta hero-cta--primary"
          tabIndex={heroVisible ? 0 : -1}
          onClick={() => {
            setHeroVisible(false);
            setCommandOpen(true);
          }}
        >
          <span>{ui.startMapping}</span>
          <i aria-hidden="true">→</i>
        </button>
        <button
          className="hero-cta hero-cta--secondary"
          tabIndex={heroVisible ? 0 : -1}
          onClick={() => {
            if (demoTourActive) {
              setDemoTourActive(false);
              focusCosmos();
              setHeroVisible(true);
              return;
            }
            setHeroVisible(false);
            setCommandOpen(false);
            setDemoTourActive(true);
          }}
        >
          <i aria-hidden="true">{demoTourActive ? "■" : "▷"}</i>
          <span>{demoTourActive ? ui.exitDemoTour : ui.demoTour}</span>
        </button>
      </motion.div>
    </motion.section>
  );
}
